import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import ReactFlow, {
  Background, ControlButton, Controls, MiniMap, ReactFlowProvider, useReactFlow,
} from 'reactflow';
import 'reactflow/dist/style.css';
import styled, { css, keyframes, useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import {
  Info, Maximize2, Minus, Network, Plus,
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import Button from '../../components/ui/Button';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import GenericFormDrawer from '../../components/GenericFormDrawer';
import LoadErrorBanner from '../../components/ui/LoadErrorBanner';
import { useRequisitionForm } from '../ats-requisitions/config/requisitionForm';
import OrgCard from './components/OrgCard';
import VacantGroupNode from './components/VacantGroupNode';
import MoreNode from './components/MoreNode';
import TeamBoxNode from './components/TeamBoxNode';
import OrgEdge from './components/OrgEdge';
import OrgBreadcrumb from './components/OrgBreadcrumb';
import OrgSearch from './components/OrgSearch';
import Toolbar from './components/Toolbar';
import TeamSummaryBar from './components/TeamSummaryBar';
import Legend from './components/Legend';
import OrgDetailsPanel from './components/OrgDetailsPanel';
import PersonView from './components/PersonView';
import AssignModal from './components/AssignModal';
import AddPositionDrawer from './components/AddPositionDrawer';
import { fetchChildren, fetchView } from './orgChartApi';
import {
  FOCUS_ME, FOCUS_ROOT, GROUP_NODE_TYPE, HIGHLIGHT_MS, MORE_NODE_TYPE, ORG_NODE_TYPE, TEAM_BOX_TYPE, VIEW_PERSON,
  VIEW_TREE, buildOrgChartSearch, buildPositionPayload, canRequestNewPositionUnder, cardVariant, collapseAll,
  departmentColor, expandIdsForRefresh, graphFromView, groupVacantSiblings, mergeChildren, mergeSubtree,
  openRequisitionUrl, parseOrgChartSearch, pathTo, visibleTree,
} from './orgChartModel';
import {
  CARD_SIZE, cameraForReveal, layoutCompactTree, markHighlighted,
} from './orgChartLayout';
import { exportFlowImage } from './orgChartExport';
import { buildOrgChartPdfLabels, buildOrgChartPdfPayload, downloadOrgChartPdf } from './orgChartPdf';

// Organizasyon Şeması (/org-chart) — spec 2026-10-24 (görünüm katmanı), kurallar spec 2026-10-15. Tek sayfa, iki görünüm:
//   • Ağaç: kökten başlar, kişinin yolu açık ve vurgulu gelir (view?focus=root&reveal=…); kompakt karma yerleşim; karta
//     tıklayınca sağda ayrıntı paneli (yönetici araçları ve Düzenle aksiyonları panelde);
//   • Kişi Odaklı: yönetici zinciri + odak + bağlılar + ekip arkadaşları (PersonView).
// Durum URL'dedir (?view&focus&edit): Kişi Odaklı'da odak değişimi push, ağaçta seçim replace.

const AT_MODULE = 'AT';
const EM_MODULE = 'EM';
const PAGE_PATH = '/org-chart';
const GUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/;

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;

const PageContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
  font-family: 'Nunito Sans', sans-serif;
  background: #fff;
  border-radius: 6px;
  overflow: hidden;
`;

const SubBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding: 8px 20px;
  min-height: 38px;
  border-bottom: 1px solid #E6EAF0;
`;

const Main = styled.div`
  flex: 1;
  min-height: 0;
  display: flex;
  position: relative;
  background: ${({ theme }) => theme.colors.orgChart.canvas};
`;

const FlowContainer = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  position: relative;
  /* Kartlar yeni konumlarına kayar, yeni açılan kartlar solarak belirir (plan: Plan aşamasında netleşenler 9).
     Dışa aktarma sırasında ikisi de kapalı — görüntüye yarım kare girmesin. */
  ${({ $animate }) => $animate && css`
    .react-flow__node { transition: transform 350ms ease; animation: ${fadeIn} 200ms ease-out; }
  `}
  .react-flow__minimap { border-radius: 12px; overflow: hidden; border: 1px solid #E6EAF0; }
  .react-flow__controls { border-radius: 12px; overflow: hidden; box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08); }
`;

const Centered = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  gap: 12px;
  padding: 24px;
  color: #8C96A0;
  font-size: 14px;
  text-align: center;
`;

const Notice = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 8px 20px 0;
  padding: 8px 12px;
  border-radius: 6px;
  background: rgba(2, 145, 239, 0.08);
  border: 1px solid rgba(2, 145, 239, 0.25);
  color: #004242;
  font-size: 13px;
`;

const FloatingNotice = styled(Notice)`
  position: absolute;
  top: 12px;
  right: 12px;
  margin: 0;
  z-index: 9;
  max-width: 420px;
  background: #fff;
`;

const ScopeTools = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-top: 12px;
`;

const nodeTypes = {
  [ORG_NODE_TYPE]: OrgCard,
  [GROUP_NODE_TYPE]: VacantGroupNode,
  [MORE_NODE_TYPE]: MoreNode,
  [TEAM_BOX_TYPE]: TeamBoxNode,
};
const edgeTypes = { orgEdge: OrgEdge };

const toggled = (set, id) => {
  const next = new Set(set);
  if (next.has(id)) next.delete(id); else next.add(id);
  return next;
};
const without = (set, ids) => {
  const list = Array.isArray(ids) ? ids : [ids];
  if (!list.some((id) => set.has(id))) return set;
  const next = new Set(set);
  list.forEach((id) => next.delete(id));
  return next;
};
// Seçili kartın çizili alt dalı: düğüm ve kenar kimlikleri (dışa aktarma süzgeci için).
export function branchOf(flow, rootId) {
  const bySource = new Map();
  flow.edges.forEach((e) => {
    if (!bySource.has(e.source)) bySource.set(e.source, []);
    bySource.get(e.source).push(e);
  });
  const nodeIds = new Set([rootId]);
  const edgeIds = new Set();
  const queue = [rootId];
  while (queue.length) {
    const id = queue.shift();
    (bySource.get(id) || []).forEach((e) => {
      edgeIds.add(e.id);
      (e.data?.kids || [e.target]).forEach((k) => {
        if (nodeIds.has(k)) return;
        nodeIds.add(k);
        queue.push(k);
      });
    });
  }
  flow.nodes.forEach((n) => { if (n.type === TEAM_BOX_TYPE && nodeIds.has(n.data?.parentId)) nodeIds.add(n.id); });
  return { nodeIds, edgeIds };
}

const crumbOf = (d) => ({
  positionId: String(d.positionId),
  label: d.isAssigned ? (d.userName || d.titleName || '-') : (d.titleName || '-'),
  title: d.titleName || undefined,
});

function OrgChartInner() {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const history = useHistory();
  const location = useLocation();
  const { hasModule, hasSeat, canRead, canWrite } = useAuth();
  const reactFlowInstance = useReactFlow();
  const flowRef = useRef(null);

  const url = useMemo(() => parseOrgChartSearch(location.search), [location.search]);
  const isTree = url.view === VIEW_TREE;
  const canEditEm = Boolean(canWrite?.(EM_MODULE));
  const editMode = isTree && url.edit && canEditEm;
  const hasAtModule = Boolean(hasModule?.(AT_MODULE));
  // Kadro talebi yazma ucu AT koltuğu da ister ([RequireModuleSeat("AT")]): koltuğu olmayana talep aksiyonu gösterilmez.
  const hasAtSeat = hasSeat ? Boolean(hasSeat(AT_MODULE)) : true;
  // AT.Write / admin başkası adına talep açabilir; değilse sunucu çağıranı sahip yapar.
  const atPrivileged = Boolean(canWrite?.(AT_MODULE));
  // ATS listesine erişebilen için açık talep rozeti bağlantıdır; diğerine düz rozet.
  const canOpenAtsList = Boolean(canRead?.(AT_MODULE) || canWrite?.(AT_MODULE));
  const palette = useMemo(() => ({
    colors: theme.colors.orgChart.departmentPalette, neutral: theme.colors.orgChart.neutral,
  }), [theme]);
  const colorOf = useCallback((d) => departmentColor(d?.departmentId, palette), [palette]);

  const [load, setLoad] = useState({ state: 'loading' });
  const [viewVersion, setViewVersion] = useState(0);
  const [collapsed, setCollapsed] = useState(() => new Set());
  const [expandedGroups, setExpandedGroups] = useState(() => new Set());
  const [loadingIds, setLoadingIds] = useState(() => new Set());
  const [notice, setNotice] = useState(null);   // null | 'notFound' | 'tooLarge' — bilgi şeridi
  const [highlightId, setHighlightId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [tooLargeIds, setTooLargeIds] = useState(() => new Set());
  const [expandAllLoading, setExpandAllLoading] = useState(null);
  const [pendingFocusId, setPendingFocusId] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pendingExport, setPendingExport] = useState(null);
  const [locked, setLocked] = useState(false);
  const [summaryKey, setSummaryKey] = useState(0);
  // Ağacın yeniden açılışı: 404 sonrası kendi pozisyonuna dönüş (URL odağı değişir ama ağaç odağa bağlı değildir).
  const [reloadKey, setReloadKey] = useState(0);
  // Kişi Odaklı'da ağaç hiç yüklenmeyebilir (doğrudan ?view=person): Beni Bul'un kendi pozisyonları oradan gelir.
  const [personOwn, setPersonOwn] = useState([]);
  const [fitPending, setFitPending] = useState(false);

  // Düzenle kipi modalları/çekmeceleri.
  const [assignPositionId, setAssignPositionId] = useState(null);
  const [addPositionDrawerOpen, setAddPositionDrawerOpen] = useState(false);
  const [addPositionParentId, setAddPositionParentId] = useState(null);
  const [deletePositionId, setDeletePositionId] = useState(null);
  const [unassignId, setUnassignId] = useState(null);
  const [mutating, setMutating] = useState(false);

  // Geri çağırmalar en güncel durumu okusun (her değişimde yeniden kurulmasınlar diye).
  const stateRef = useRef({});
  stateRef.current = {
    load, collapsed, selectedId, url,
  };
  // Her görünüm isteği sırayı artırır; geç gelen yanıt yeni görünümün (kip değişti) üstüne YAZILMAZ.
  const viewSeq = useRef(0);
  // Yüklenmiş çocuklar OTURUM boyunca saklanır; kip ayrı anahtardır; her mutasyon önbelleği boşaltır.
  const childCache = useRef(new Map());

  const navigate = useCallback((next, { replace = false } = {}) => {
    setNotice(null);
    const { url: current } = stateRef.current;
    const search = buildOrgChartSearch({ view: current.view, focus: current.focus, edit: editMode, ...next });
    if (replace) history.replace({ pathname: PAGE_PATH, search });
    else history.push({ pathname: PAGE_PATH, search });
  }, [history, editMode]);

  const fallBackToMe = useCallback(() => {
    setNotice('notFound');
    setPendingExport(null);
    const { url: current } = stateRef.current;
    history.replace({ pathname: PAGE_PATH, search: buildOrgChartSearch({ view: current.view, focus: FOCUS_ME, edit: editMode }) });
    setReloadKey((k) => k + 1);
  }, [history, editMode]);

  // Eski bağlantılar (`depth`) ve yetkisiz `edit=1` URL'den düşer (spec 2026-10-24 §4.1).
  useEffect(() => {
    const raw = new URLSearchParams(location.search);
    const stale = raw.has('depth') || !raw.has('view') || (raw.get('edit') === '1' && !editMode);
    if (stale) {
      history.replace({ pathname: PAGE_PATH, search: buildOrgChartSearch({ view: url.view, focus: url.focus, edit: editMode }) });
    }
  }, [location.search, url, editMode, history]);

  // ── Ağaç verisi ──

  // Kök görünümü + reveal (kökten kişiye yol) + açık dallar. Sonuç: { selected } ya da null (geçersiz/eski yanıt).
  const fetchTree = useCallback(async ({ reveal, expand } = {}) => {
    const seq = ++viewSeq.current;
    const target = reveal && reveal !== FOCUS_ROOT ? reveal : undefined;
    const result = await fetchView({
      focus: FOCUS_ROOT, depth: '1', edit: editMode, reveal: target, expand,
    });
    if (seq !== viewSeq.current) return null;
    if (result.state === 'notFound') return { notFound: true };
    if (result.state !== 'ok') return { error: true };
    setLoad({ state: 'ok', view: result.data, graph: graphFromView(result.data), edit: editMode });
    return { selected: result.data.revealId ? String(result.data.revealId) : null, view: result.data };
  }, [editMode]);

  // Açılış: görünüm ağaca geçince ya da kip değişince. Ağaçtaki seçim değişimi (URL replace) yeniden istek ATMAZ.
  // Yalnız Düzenle açılıp kapandıysa açık dallar (expand), seçim ve kamera korunur.
  const lastOpen = useRef({ isTree: null, reloadKey: null });
  useEffect(() => {
    if (!isTree) {
      lastOpen.current = { isTree, reloadKey };
      return undefined;
    }
    let alive = true;
    const { url: current, load: loaded, collapsed: closed } = stateRef.current;
    const keepView = loaded.state === 'ok' && lastOpen.current.isTree === true && lastOpen.current.reloadKey === reloadKey;
    lastOpen.current = { isTree, reloadKey };
    const focus = current.focus;
    if (focus !== FOCUS_ME && focus !== FOCUS_ROOT && !GUID.test(focus)) {
      fallBackToMe();
      return undefined;
    }
    setLoad((prev) => (prev.state === 'ok' ? prev : { state: 'loading' }));
    const expand = keepView ? expandIdsForRefresh(loaded.graph, closed) : undefined;
    fetchTree({ reveal: focus, expand }).then((res) => {
      if (!alive || !res) return;
      if (res.notFound && focus !== FOCUS_ME) {
        fallBackToMe();
        return;
      }
      if (res.error || res.notFound) {   // kendi pozisyonu bile bulunamadıysa döngüye girmez: hata ekranı
        setPendingExport(null);
        setLoad({ state: 'error' });
        return;
      }
      setSelectedId(res.selected);
      if (keepView) return;
      setCollapsed(new Set());
      setExpandedGroups(new Set());
      setTooLargeIds(new Set());
      setPanelOpen(Boolean(res.selected));
      setViewVersion((v) => v + 1);
    });
    return () => { alive = false; };
    // url.focus bilinçli olarak bağımlılık DEĞİL: ağaçta seçim URL'yi replace eder, yeniden yükleme yapmaz.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTree, editMode, reloadKey, fetchTree, fallBackToMe]);

  // Mutasyon / talep sonrası: tek istekte, açık düğümlerle (expand) yenilenir; açık/kapalı, seçim ve kamera korunur.
  // `reveal: null` seçimi bırakır (silinen pozisyon).
  const refresh = useCallback(async (extraExpand = [], { reveal } = {}) => {
    const { load: current, collapsed: closed, selectedId: selected } = stateRef.current;
    if (current.state !== 'ok') return;
    childCache.current.clear();
    const target = reveal === undefined ? selected : reveal;
    const expand = expandIdsForRefresh(current.graph, closed, extraExpand);
    const res = await fetchTree({ reveal: target || undefined, expand });
    if (!res) return;
    if (res.notFound && target) {
      setSelectedId(null);
      setPanelOpen(false);
      await fetchTree({ expand });
    }
  }, [fetchTree]);

  // ── Açma / kapama, sayfalar ──

  const mergeInto = useCallback((parentId, payload, page) => {
    setLoad((prev) => (prev.state === 'ok' ? { ...prev, graph: mergeChildren(prev.graph, parentId, payload, page) } : prev));
  }, []);

  const loadChildren = useCallback(async (parentId, page, loadingKey) => {
    const seq = viewSeq.current;
    const cacheKey = `${editMode ? 'e' : 'v'}:${parentId}:${page}`;
    let payload = childCache.current.get(cacheKey);
    if (!payload) {
      setLoadingIds((prev) => new Set(prev).add(loadingKey));
      const result = await fetchChildren(parentId, { edit: editMode, page });
      setLoadingIds((prev) => without(prev, loadingKey));
      if (result.state !== 'ok') return false;
      payload = result.data;
      childCache.current.set(cacheKey, payload);
    }
    if (seq !== viewSeq.current) return false;   // eski görünümün (ya da kipin) yanıtı
    mergeInto(parentId, payload, page);
    return true;
  }, [editMode, mergeInto]);

  const handleToggleChildren = useCallback(async (positionId) => {
    const id = String(positionId);
    const { load: current } = stateRef.current;
    if (current.state !== 'ok') return;
    if (current.graph.loaded[id]) {
      setCollapsed((prev) => toggled(prev, id));
      return;
    }
    if (await loadChildren(id, 1, id)) setCollapsed((prev) => without(prev, id));
  }, [loadChildren]);

  const handleLoadReports = useCallback(async (positionId) => {
    const id = String(positionId);
    const { load: current } = stateRef.current;
    if (current.state !== 'ok') return;
    if (current.graph.loaded[id] || await loadChildren(id, 1, id)) setCollapsed((prev) => without(prev, id));
  }, [loadChildren]);

  const handleLoadMore = useCallback(async (parentId) => {
    const id = String(parentId);
    const { load: current } = stateRef.current;
    if (current.state !== 'ok') return;
    await loadChildren(id, (current.graph.pages[id] || 1) + 1, `more:${id}`);
  }, [loadChildren]);

  const handleToggleGroup = useCallback((groupKey) => setExpandedGroups((prev) => toggled(prev, groupKey)), []);

  // ── Seçim, yol, arama ──

  const selectCard = useCallback((positionId, { center = false, highlight = false } = {}) => {
    const id = String(positionId);
    const { load: current } = stateRef.current;
    // Seçilen kartın ataları açılır (panelden ya da Konum satırından kapalı bir dala atlanabilir).
    if (current.state === 'ok') setCollapsed((prev) => without(prev, pathTo(current.graph, id).slice(0, -1)));
    setSelectedId(id);
    setPanelOpen(true);
    if (center) setPendingFocusId(id);
    if (highlight) setHighlightId(id);
    if (stateRef.current.url.focus !== id) navigate({ focus: id }, { replace: true });
  }, [navigate]);

  const handleSelect = useCallback((positionId) => selectCard(positionId), [selectCard]);
  const handleSelectCentered = useCallback((positionId) => selectCard(positionId, { center: true }), [selectCard]);

  // Arama, Beni Bul: kişiye giden yol sunucudan açılır (reveal), açık dallar korunur (expand).
  const revealInTree = useCallback(async (positionId) => {
    const { load: current, collapsed: closed } = stateRef.current;
    const expand = current.state === 'ok' ? expandIdsForRefresh(current.graph, closed) : [];
    const res = await fetchTree({ reveal: String(positionId), expand });
    if (!res) return;
    if (res.notFound) {
      fallBackToMe();
      return;
    }
    if (res.error || !res.selected) return;
    const graph = graphFromView(res.view);
    setCollapsed((prev) => without(prev, pathTo(graph, res.selected)));
    selectCard(res.selected, { center: true, highlight: true });
  }, [fetchTree, fallBackToMe, selectCard]);

  const handleSearchSelect = useCallback((positionId) => {
    if (isTree) revealInTree(positionId);
    else navigate({ focus: String(positionId) });
  }, [isTree, revealInTree, navigate]);

  const handleMe = useCallback((positionId) => {
    if (isTree) revealInTree(positionId);
    else navigate({ focus: String(positionId) });
  }, [isTree, revealInTree, navigate]);

  const handleCollapseAll = useCallback(() => {
    const { load: current } = stateRef.current;
    if (current.state !== 'ok') return;
    setCollapsed(collapseAll(current.graph));
    setSelectedId(null);
    setPanelOpen(false);
    setFitPending(true);
    navigate({ focus: FOCUS_ROOT }, { replace: true });   // seçimsiz ağaç: URL'de eski seçim kalmasın
  }, [navigate]);

  // "Alt Dalı Tamamen Aç" (spec 2026-10-24 §7.1): depth=all yanıtı grafa katılır; 400 → şerit, o kartta düğme kapanır.
  const handleExpandAll = useCallback(async (positionId) => {
    const id = String(positionId);
    const seq = viewSeq.current;
    setExpandAllLoading(id);
    const result = await fetchView({ focus: id, depth: 'all', edit: editMode });
    setExpandAllLoading(null);
    if (seq !== viewSeq.current) return false;
    if (result.state === 'tooLarge') {
      setTooLargeIds((prev) => new Set(prev).add(id));
      setNotice('tooLarge');
      return false;
    }
    if (result.state !== 'ok') return false;
    setLoad((prev) => (prev.state === 'ok' ? { ...prev, graph: mergeSubtree(prev.graph, result.data) } : prev));
    setCollapsed((prev) => without(prev, [id, ...(result.data.loadedParentIds || []).map(String)]));
    return true;
  }, [editMode]);

  useEffect(() => {
    if (!highlightId) return undefined;
    const timer = setTimeout(() => setHighlightId(null), HIGHLIGHT_MS);
    return () => clearTimeout(timer);
  }, [highlightId]);

  // ── Görünüm geçişleri ──

  const handleViewChange = useCallback((view) => {
    const { url: current, selectedId: selected } = stateRef.current;
    if (view === current.view) return;
    if (view === VIEW_PERSON) navigate({ view: VIEW_PERSON, focus: selected || current.focus, edit: false });
    else navigate({ view: VIEW_TREE });
  }, [navigate]);

  const openPerson = useCallback((positionId) => {
    navigate({ view: VIEW_PERSON, focus: String(positionId), edit: false });
  }, [navigate]);

  // ── Kadro talebi (sayfa içi form — eski Ekibimin Şeması akışı aynen) ──

  const {
    objectSchema, buildPayload, noteHiringManager, buildPrefill,
  } = useRequisitionForm({ writable: atPrivileged, ownerLocked: !atPrivileged });
  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null);

  const openForm = useCallback((query) => {
    Promise.resolve(buildPrefill(query))
      .catch(() => null)
      .then((init) => {
        const values = init || { positionMode: query.mode };
        noteHiringManager(values.hiringManagerUserId ?? null);
        setFormInitial(values);
        setFormOpen(true);
      });
  }, [buildPrefill, noteHiringManager]);
  const handleRequestHire = useCallback((positionId) => openForm({ mode: 'existing', positionId }), [openForm]);
  const handleRequestNewPosition = useCallback((parentPositionId) => openForm({ mode: 'new', parentPositionId }), [openForm]);
  const handleOpenRequisition = useCallback((requisitionId) => history.push(openRequisitionUrl(requisitionId)), [history]);
  const closeForm = useCallback(() => {
    setFormOpen(false);
    setFormInitial(null);
  }, []);

  // POST hata yolunda REJECT eder (api.js), toast'ı interceptor atar — burada toast YOK.
  const handleSave = useCallback(async (formData) => {
    let res;
    try {
      res = await api.post('/v1/ats-requisitions', buildPayload(formData));
    } catch {
      return false;
    }
    if (res?.data?.status !== true) return false;
    closeForm();
    setSummaryKey((k) => k + 1);
    await refresh();
    return true;
  }, [buildPayload, closeForm, refresh]);

  // ── Düzenle kipi (mutasyonlar aynen; yenileme `refresh`) ──

  const nodeData = useCallback((positionId) => {
    const { load: current } = stateRef.current;
    return current.state === 'ok' ? current.graph.byId[String(positionId)]?.data : undefined;
  }, []);

  const handleAssign = useCallback((positionId) => setAssignPositionId(positionId), []);
  const handleUnassign = useCallback((assignmentId) => setUnassignId(assignmentId), []);
  const handleAddChild = useCallback((parentId) => {
    setAddPositionParentId(parentId);
    setAddPositionDrawerOpen(true);
  }, []);
  const handleAddRoot = useCallback(() => {
    if (!editMode) navigate({ view: VIEW_TREE, edit: true }, { replace: true });
    setAddPositionParentId(null);
    setAddPositionDrawerOpen(true);
  }, [editMode, navigate]);
  const handleDeletePosition = useCallback((positionId) => setDeletePositionId(positionId), []);

  // Kopyalama: kod GÖNDERİLMEZ (omitCode — sunucu yeni benzersiz kod üretir); planlanan kadro taşınır.
  const handleDuplicate = useCallback(async (data) => {
    try {
      const res = await api.post('/v1/positions', buildPositionPayload(data, undefined, { omitCode: true }));
      const createdId = res.data?.data?.id;
      if (createdId) setPendingFocusId(String(createdId));
      await refresh([data.parentPositionId]);
    } catch { /* interceptor */ }
  }, [refresh]);

  // Ekleme başarısı: yeni kart görünsün diye üstü açılır ve yenilemede genişletilir; kamera yeni karta kayar.
  const handleAddSuccess = useCallback(async (created) => {
    if (created?.id) setPendingFocusId(String(created.id));
    if (addPositionParentId != null) {
      const parentKey = String(addPositionParentId);
      setCollapsed((prev) => without(prev, parentKey));
      await refresh([parentKey]);
    } else {
      await refresh();
    }
  }, [addPositionParentId, refresh]);

  const confirmDelete = async () => {
    if (!deletePositionId) return;
    setMutating(true);
    const deletingSelected = String(deletePositionId) === stateRef.current.selectedId;
    try {
      await api.delete(`/v1/positions/${deletePositionId}`);
      if (deletingSelected) {
        setSelectedId(null);
        setPanelOpen(false);
        navigate({ focus: FOCUS_ROOT }, { replace: true });   // silinen pozisyon URL'de kalmasın (sonra 404 şeridi verirdi)
      }
      await refresh([], deletingSelected ? { reveal: null } : {});
    } catch { /* interceptor */ } finally {
      setMutating(false);
      setDeletePositionId(null);
    }
  };

  const confirmUnassign = async () => {
    if (!unassignId) return;
    setMutating(true);
    try {
      await api.delete(`/v1/user-positions/${unassignId}`);
      await refresh();
    } catch { /* interceptor */ } finally {
      setMutating(false);
      setUnassignId(null);
    }
  };

  // PUT yükü düğümün güncel verisinden (buildPositionPayload — PlannedHeadcount düşmesin).
  const handleDetach = useCallback(async (positionId) => {
    try {
      await api.put(`/v1/positions/${positionId}`, buildPositionPayload(nodeData(positionId), { parentPositionId: null }));
      await refresh();
    } catch { /* interceptor */ }
  }, [nodeData, refresh]);

  const handleReParent = useCallback(async (childId, newParentId) => {
    if (childId === newParentId) return;
    try {
      await api.put(`/v1/positions/${childId}`, buildPositionPayload(nodeData(childId), { parentPositionId: newParentId }));
      await refresh([newParentId]);
    } catch { /* interceptor */ }
  }, [nodeData, refresh]);

  // ReactFlow onConnect: kaynak = üst (alt tutamaçtan), hedef = çocuk (üst tutamağa).
  const onConnect = useCallback((params) => handleReParent(params.target, params.source), [handleReParent]);

  // ── Çizim ──

  // Kartlar ve panel YALNIZ düzenle verisi yüklüyken düzenle kipindedir (URL'de edit=1 olsa da veri gelene kadar değil):
  // görüntüle verisinde koltuk kodu ve planlanan kadro yoktur, o veriyle yapılan PUT onları silerdi.
  const dataEdit = editMode && load.state === 'ok' && Boolean(load.edit);
  const graph = load.state === 'ok' ? load.graph : null;
  const pathIds = useMemo(() => new Set(graph ? pathTo(graph, selectedId) : []), [graph, selectedId]);

  const flow = useMemo(() => {
    if (!graph) return { nodes: [], edges: [] };
    const tree = visibleTree(graph, { collapsed });
    const grouped = groupVacantSiblings(tree.nodes, tree.edges, expandedGroups, new Set([selectedId, highlightId].filter(Boolean)));
    const decorated = grouped.nodes.map((n) => {
      if (n.type === GROUP_NODE_TYPE) return { ...n, data: { ...n.data, onToggleGroup: handleToggleGroup } };
      if (n.type === MORE_NODE_TYPE) {
        return { ...n, data: { ...n.data, loading: loadingIds.has(`more:${n.data.parentId}`), onLoadMore: handleLoadMore } };
      }
      const d = n.data || {};
      return {
        ...n,
        type: ORG_NODE_TYPE,
        data: {
          ...d,
          variant: cardVariant(d, dataEdit),
          color: colorOf(d),
          isSelected: n.id === selectedId,
          isExpanded: Boolean(graph.loaded[n.id]) && !collapsed.has(n.id),
          loadingChildren: loadingIds.has(n.id),
          highlight: highlightId === n.id,
          connectable: dataEdit && !locked,
          onSelect: handleSelect,
          onToggleChildren: handleToggleChildren,
        },
      };
    });
    const laid = layoutCompactTree(decorated, grouped.edges);
    // Vurgulu yol en üstte çizilsin.
    const edges = markHighlighted(laid.edges, pathIds);
    return { nodes: laid.nodes, edges: [...edges.filter((e) => !e.data.highlight), ...edges.filter((e) => e.data.highlight)] };
  }, [
    graph, collapsed, expandedGroups, loadingIds, highlightId, dataEdit, locked, selectedId, pathIds, colorOf,
    handleToggleGroup, handleLoadMore, handleSelect, handleToggleChildren,
  ]);

  // Kamera: YALNIZ yeni görünümde (açılış, kip) — açma/kapama ve yenileme kamerayı sıfırlamaz.
  useEffect(() => {
    if (!viewVersion || load.state !== 'ok' || !reactFlowInstance) return;
    const rect = flowRef.current?.getBoundingClientRect();
    const camera = cameraForReveal({
      nodes: flow.nodes, focusId: selectedId, width: rect?.width, height: rect?.height,
    });
    if (camera) reactFlowInstance.setViewport(camera, { duration: 250 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewVersion]);

  // Seçim / ekleme / kopyalama sonrası: kart çizilince ona kayılır. Yakınlaştırma korunur; yalnız kartlar okunmaz
  // küçüklükteyse (< 0.5) okunur düzeye (0.8) çıkılır.
  useEffect(() => {
    if (!pendingFocusId || !reactFlowInstance) return;
    const target = flow.nodes.find((n) => n.id === pendingFocusId);
    if (!target) {
      // Grafikte olup çizilmeyen kart için bekleme sürmez (sonradan beklenmedik kamera sıçraması olmasın); henüz grafa
      // girmemiş kart (ekleme/kopyalama yenilemesi) beklenir.
      if (graph?.byId[pendingFocusId]) setPendingFocusId(null);
      return;
    }
    const zoom = reactFlowInstance.getZoom?.() || 0.8;
    reactFlowInstance.setCenter(target.position.x + CARD_SIZE.width / 2, target.position.y + CARD_SIZE.height / 2, {
      zoom: zoom < 0.5 ? 0.8 : zoom,
      duration: 450,
    });
    setPendingFocusId(null);
  }, [pendingFocusId, flow, graph, reactFlowInstance]);

  // "Hepsini Daralt" sonrası sığdırma: yeni yerleşim React Flow'un deposuna girdikten SONRA (iki kare).
  useEffect(() => {
    if (!fitPending || !reactFlowInstance) return undefined;
    let cancelled = false;
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      if (!cancelled) reactFlowInstance.fitView?.({ duration: 300, padding: 0.1, minZoom: 0.25, maxZoom: 1 });
    }));
    setFitPending(false);
    return () => { cancelled = true; };
  }, [fitPending, flow, reactFlowInstance]);

  // ── Dışa aktarma (spec 2026-10-24 §10) ──

  // "Seçili Dal": yalnız seçili kart ve onun çizili alt dalı (kartlar, ekip kutuları, "Daha Göster"/grup kartları ve bu
  // dalın çizgileri) görüntüye girer; çerçeve de yalnız onlardan kurulur.
  const flowStateRef = useRef(flow);
  flowStateRef.current = flow;
  // PDF sunucuda (QuestPDF): ekranda çizili yerleşim (konum, renk, çizgi yolları) gönderilir; kart içeriklerini sunucu
  // görünürlük kuralıyla kendisi okur. DOM'dan görüntü alınmaz — ekran dışındaki kartlar da yerleşimde zaten vardır.
  const runPdfExport = useCallback(async (branchRootId) => {
    const current = flowStateRef.current;
    const payload = buildOrgChartPdfPayload({
      nodes: current.nodes,
      edges: current.edges,
      include: branchRootId ? branchOf(current, branchRootId) : null,
      labels: buildOrgChartPdfLabels(t),
      lang: i18n?.language,
      edit: dataEdit,
      branchRootId,
    });
    setPdfBusy(true);
    try {
      await downloadOrgChartPdf(payload, `${t('orgChart.pdf.docTitle')}.pdf`);
    } finally {
      setPdfBusy(false);
    }
  }, [t, i18n, dataEdit]);

  const runExport = useCallback((format, branchRootId = null) => {
    if (format === 'pdf') {
      runPdfExport(branchRootId);
      return;
    }
    setExporting(true);   // onlyRenderVisibleElements ve animasyonlar kapanır: ekran dışındaki kartlar da DOM'a girer
    window.requestAnimationFrame(() => window.requestAnimationFrame(() => {
      const include = branchRootId ? branchOf(flowStateRef.current, branchRootId) : null;
      const all = reactFlowInstance.getNodes();
      const nodes = include ? all.filter((n) => include.nodeIds.has(n.id)) : all;
      Promise.resolve(exportFlowImage({
        flowEl: flowRef.current, nodes, include,
      })).finally(() => setExporting(false));
    }));
  }, [reactFlowInstance, runPdfExport]);

  const handleExport = useCallback(async (format, scope) => {
    if (scope !== 'branch') {
      runExport(format);
      return;
    }
    const { selectedId: selected } = stateRef.current;
    if (!selected) return;
    if (await handleExpandAll(selected)) setPendingExport({ format, branchRootId: selected });
  }, [runExport, handleExpandAll]);

  // "Seçili Dal": alt dal açılıp çizilince dışa aktarılır; ağaç o hâlde kalır.
  useEffect(() => {
    if (!pendingExport || load.state !== 'ok') return;
    const { format, branchRootId } = pendingExport;
    setPendingExport(null);
    runExport(format, branchRootId);
  }, [pendingExport, load.state, runExport]);

  // ── Görünüm ──

  const view = load.state === 'ok' ? load.view : null;
  const flowNode = selectedId ? flow.nodes.find((n) => n.id === selectedId) : null;
  const selectedData = flowNode?.data || (graph && selectedId ? graph.byId[selectedId]?.data : null);
  const crumbs = graph && selectedId ? pathTo(graph, selectedId).map((id) => crumbOf(graph.byId[id].data)) : [];
  const rootsTruncated = view && typeof view.rootTotalCount === 'number' && view.rootTotalCount > (graph?.rootIds.length || 0);
  const canExportBranch = Boolean(selectedData && selectedData.directCount > 0 && !selectedData.allDepthTooLarge
    && !tooLargeIds.has(selectedId));

  const scopePerms = (d) => ({
    canRequestHire: Boolean(d?.canRequestHire) && hasAtSeat,
    canRequestNewPosition: canRequestNewPositionUnder(d, hasAtModule && hasAtSeat),
    canOpenRequisition: canOpenAtsList,
    locked,
  });

  const renderPersonScopeTools = (d) => {
    const perms = scopePerms(d);
    const hire = d.inManagedScope && perms.canRequestHire && !d.openRequisitionId;
    if (!hire && !perms.canRequestNewPosition) return null;
    return (
      <ScopeTools data-testid="person-scope-tools">
        {hire && (
          <Button data-testid="person-request-hire" onClick={() => handleRequestHire(String(d.positionId))}>
            {t('orgChart.scope.requestHire')}
          </Button>
        )}
        {perms.canRequestNewPosition && (
          <Button variant="outline" data-testid="person-request-new-position"
            onClick={() => handleRequestNewPosition(String(d.positionId))}>
            {t('orgChart.scope.requestNewPosition')}
          </Button>
        )}
      </ScopeTools>
    );
  };

  const formDrawer = (
    <GenericFormDrawer
      open={formOpen}
      onClose={closeForm}
      title={t('ats.requisition.new')}
      schema={objectSchema}
      initialValues={formInitial}
      onSave={handleSave}
    />
  );

  let body;
  if (!isTree) {
    body = (
      <PersonView
        focus={url.focus}
        colorOf={colorOf}
        onFocus={(id) => navigate({ focus: id })}
        onShowInTree={(id) => navigate({ view: VIEW_TREE, focus: id || url.focus })}
        onNotFound={fallBackToMe}
        renderScopeTools={renderPersonScopeTools}
        onLoaded={(v) => setPersonOwn(v?.own || [])}
      />
    );
  } else if (load.state === 'loading') {
    body = (
      <Centered data-testid="org-chart-loading">
        <CircularProgress size={20} />
        {t('orgChart.loading')}
      </Centered>
    );
  } else if (load.state === 'error') {
    body = (
      <div data-testid="org-chart-error" style={{ flex: 1 }}>
        <LoadErrorBanner message={t('orgChart.loadFailed')} />
      </div>
    );
  } else if (flow.nodes.length === 0) {
    // Kiracıda pozisyon var ama bu kipte hepsi gizli (her koltuk boş): EM.Write'a Kök Ekle DEĞİL Düzenle önerilir.
    const allHidden = Boolean(view?.tenantHasPositions);
    let emptyKey = 'orgChart.emptyChart';
    if (canEditEm) emptyKey = allHidden ? 'orgChart.allHiddenUseEdit' : 'orgChart.noPositions';
    body = (
      <Centered data-testid="org-chart-empty" data-reason={allHidden ? 'all-hidden' : 'no-positions'}>
        <Network size={28} />
        <div>{t(emptyKey)}</div>
        {canEditEm && allHidden && !editMode && (
          <Button variant="outline" data-testid="empty-open-edit" onClick={() => navigate({ edit: true }, { replace: true })}>
            {t('orgChart.edit')}
          </Button>
        )}
        {canEditEm && !allHidden && (
          <Button variant="outline" data-testid="empty-add-root" onClick={handleAddRoot}>{t('orgChart.addRoot')}</Button>
        )}
      </Centered>
    );
  } else {
    body = (
      <>
        <FlowContainer ref={flowRef} data-testid="org-chart" $animate={!exporting}>
          {rootsTruncated && (
            <FloatingNotice data-testid="roots-truncated">
              <Info size={14} />
              {t('orgChart.rootsTruncated', { shown: graph.rootIds.length, total: view.rootTotalCount })}
            </FloatingNotice>
          )}
          <ReactFlow
            nodes={flow.nodes}
            edges={flow.edges}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            onConnect={dataEdit && !locked ? onConnect : undefined}
            minZoom={0.25}
            maxZoom={1.5}
            zoomOnScroll
            panOnScroll={false}
            panOnDrag={!(dataEdit && locked)}
            nodesDraggable={false}
            nodesConnectable={dataEdit && !locked}
            edgesFocusable={false}
            elementsSelectable={false}
            onlyRenderVisibleElements={!exporting}
            proOptions={{ hideAttribution: true }}
          >
            <Background variant="dots" gap={22} size={1} color="#DCE2EA" />
            <MiniMap
              position="bottom-left"
              pannable
              zoomable
              nodeColor={(n) => {
                if (n.type === TEAM_BOX_TYPE) return 'transparent';
                return pathIds.has(n.id) ? theme.colors.primary.main : (n.data?.color || theme.colors.orgChart.neutral);
              }}
              nodeStrokeWidth={0}
              maskColor="rgba(245, 247, 250, 0.7)"
            />
            {/* React Flow'un hazır düğmeleri İngilizce ipucu taşır — çevirili ControlButton'lar. */}
            <Controls position="bottom-right" showZoom={false} showFitView={false} showInteractive={false}>
              <ControlButton data-testid="zoom-in" title={t('orgChart.zoomIn')} onClick={() => reactFlowInstance.zoomIn({ duration: 200 })}>
                <Plus size={14} />
              </ControlButton>
              <ControlButton data-testid="zoom-out" title={t('orgChart.zoomOut')} onClick={() => reactFlowInstance.zoomOut({ duration: 200 })}>
                <Minus size={14} />
              </ControlButton>
              <ControlButton
                data-testid="zoom-fit"
                title={t('orgChart.fitView')}
                onClick={() => reactFlowInstance.fitView({ duration: 300, padding: 0.1, minZoom: 0.25, maxZoom: 1 })}
              >
                <Maximize2 size={14} />
              </ControlButton>
            </Controls>
          </ReactFlow>
        </FlowContainer>
        {panelOpen && selectedData && (
          <OrgDetailsPanel
            node={selectedData}
            manager={graph.byId[graph.parentOf[selectedId]]?.data || null}
            directReports={graph.loaded[selectedId]
              ? (graph.children[selectedId] || []).map((id) => graph.byId[id]?.data).filter(Boolean)
              : null}
            variant={cardVariant(selectedData, dataEdit)}
            color={colorOf(selectedData)}
            colorOf={colorOf}
            perms={scopePerms(selectedData)}
            expandAllBlocked={tooLargeIds.has(selectedId)}
            reportsLoading={loadingIds.has(selectedId)}
            expandAllLoading={expandAllLoading === selectedId}
            onClose={() => setPanelOpen(false)}
            onSelect={handleSelectCentered}
            onLoadReports={handleLoadReports}
            onOpenPerson={openPerson}
            onExpandAll={handleExpandAll}
            onRequestHire={handleRequestHire}
            onRequestNewPosition={handleRequestNewPosition}
            onOpenRequisition={handleOpenRequisition}
            onToggleGroup={handleToggleGroup}
            onAssign={handleAssign}
            onUnassign={handleUnassign}
            onAddChild={handleAddChild}
            onDuplicate={handleDuplicate}
            onDeletePosition={handleDeletePosition}
            onDetach={handleDetach}
          />
        )}
      </>
    );
  }

  return (
    <PageContainer>
      <Toolbar
        view={url.view}
        onViewChange={handleViewChange}
        searchSlot={<OrgSearch inline onSelect={handleSearchSelect} edit={editMode} />}
        ownPositions={(view?.own || personOwn).map((o) => ({ ...o, positionId: String(o.positionId) }))}
        onMe={handleMe}
        onCollapseAll={handleCollapseAll}
        onExport={handleExport}
        exporting={exporting || pdfBusy}
        canExportBranch={canExportBranch}
        canEdit={canEditEm}
        editMode={editMode}
        onToggleEdit={() => navigate({ edit: !editMode }, { replace: true })}
        locked={locked}
        setLocked={setLocked}
        onAddRoot={handleAddRoot}
        legendSlot={isTree ? <Legend nodes={flow.nodes} /> : null}
      />
      {isTree && (
        <SubBar>
          <OrgBreadcrumb crumbs={crumbs} onSelect={handleSelectCentered} emptyLabel={t('orgChart.allCompany')} />
          <TeamSummaryBar reloadKey={summaryKey} />
        </SubBar>
      )}
      {notice && (
        <Notice data-testid={notice === 'tooLarge' ? 'branch-too-large' : 'focus-not-found'}>
          <Info size={14} />
          {t(notice === 'tooLarge' ? 'orgChart.branchTooLargeShown' : 'orgChart.focusNotFound')}
        </Notice>
      )}
      {formDrawer}
      <Main>{body}</Main>

      {canEditEm && (
        <>
          <AssignModal
            isOpen={assignPositionId != null}
            positionId={assignPositionId}
            onClose={() => setAssignPositionId(null)}
            onSuccess={() => refresh()}
          />
          <AddPositionDrawer
            isOpen={addPositionDrawerOpen}
            // Üst kartın verisi: etiket (ünvan · departman · oturan kişi · kod) ve departman varsayılanı buradan gelir.
            parentPosition={addPositionParentId
              ? { ...(nodeData(addPositionParentId) || {}), positionId: addPositionParentId }
              : null}
            onClose={() => setAddPositionDrawerOpen(false)}
            onSuccess={handleAddSuccess}
          />
          <ConfirmDialog
            isOpen={deletePositionId != null}
            title={t('orgChart.deletePosition')}
            message={t('orgChart.deleteConfirm')}
            onConfirm={confirmDelete}
            onCancel={() => setDeletePositionId(null)}
            confirmText={t('position.delete')}
            cancelText={t('common.cancel')}
            loading={mutating}
          />
          <ConfirmDialog
            isOpen={unassignId != null}
            title={t('orgChart.unassign')}
            message={t('orgChart.unassignConfirm')}
            onConfirm={confirmUnassign}
            onCancel={() => setUnassignId(null)}
            confirmText={t('orgChart.unassign')}
            cancelText={t('common.cancel')}
            loading={mutating}
          />
        </>
      )}
    </PageContainer>
  );
}

export default function OrgChartPage() {
  return (
    <ReactFlowProvider>
      <OrgChartInner />
    </ReactFlowProvider>
  );
}

